<script setup>
import moment from 'moment'
import AppDateTimePicker from '@core/components/AppDateTimePicker.vue'
import { requiredValidator } from '@validators'

// Inline editor for a single membership on a member's own record. Used from both
// the Platinum Memberships list and the Upcoming Memberships list so the two
// offer exactly the same controls.
//
// It only collects values — the term, status and plan details are recomputed
// server-side (lib/subscriptions.recomputeFixedTermSubscription) so an edited
// membership lands in the same state as one granted the same way.
defineProps({
  modelValue: { type: Object, required: true },
  planOptions: { type: Array, required: true },
  saving: { type: Boolean, default: false },
  error: { type: String, default: null },
})

defineEmits(['update:modelValue', 'save', 'cancel'])
</script>

<template>
  <div class="membership-editor mt-3">
    <VSelect
      :model-value="modelValue.planId"
      label="Plan"
      :items="planOptions"
      item-title="text"
      item-value="value"
      density="comfortable"
      class="mb-3"
      @update:model-value="$emit('update:modelValue', { ...modelValue, planId: $event })"
    />

    <AppDateTimePicker
      :model-value="modelValue.firstBillingDate"
      label="Start Date"
      class="mb-3"
      :config="{ disableMobile: true, altInput: true, altFormat: 'l, n/j', dateFormat: 'Y-m-d' }"
      :rules="[requiredValidator]"
      @update:model-value="$emit('update:modelValue', { ...modelValue, firstBillingDate: $event })"
    />

    <VTextField
      :model-value="modelValue.membershipMessage"
      label="Membership Message"
      density="comfortable"
      class="mb-2"
      @update:model-value="$emit('update:modelValue', { ...modelValue, membershipMessage: $event })"
    />

    <div class="text-caption text-medium-emphasis mb-3">
      The term is recalculated from the start date and plan. A start date in the
      past takes effect immediately; if its term has already run out the
      membership is recorded as complete.
    </div>

    <VAlert
      v-if="error"
      type="error"
      density="compact"
      class="mb-3"
    >
      {{ error }}
    </VAlert>

    <div class="d-flex ga-2">
      <VBtn
        color="primary"
        size="small"
        :loading="saving"
        @click="$emit('save')"
      >
        Save
      </VBtn>
      <VBtn
        variant="text"
        size="small"
        :disabled="saving"
        @click="$emit('cancel')"
      >
        Cancel
      </VBtn>
    </div>
  </div>
</template>

<style lang="scss" scoped>
// 16px minimum, or iOS Safari zooms the page when the field takes focus.
.membership-editor :deep(input),
.membership-editor :deep(.v-field__input) {
  font-size: 16px;
}
</style>
